"use client"

import { useLocale } from "next-intl"
import { useCallback, useMemo } from "react"
import { useCountry } from "@/components/layout/country-provider"
import { membershipFirstCheckerStrings } from "./membership-first-services"
import type { Locale } from "./insurance-types"
import de from "@/data/insurance/i18n/de.json"
import en from "@/data/insurance/i18n/en.json"
import fr from "@/data/insurance/i18n/fr.json"

/**
 * Translation hook for the ported insurance coverage checker.
 *
 * The checker's copy uses flat dot-notation keys (e.g. `"gate.success.desc"`
 * AND `"gate.success.desc.known"`) with parent/leaf overlaps that next-intl's
 * nested message model cannot represent — and `{{param}}` mustache
 * interpolation rather than ICU. So the copy is bundled as static JSON here
 * (SSR-rendered, no runtime fetch / no flash — unlike the old Locize layer)
 * and looked up directly, keeping the checker's exact fallback + param
 * semantics. Locale comes from next-intl routing.
 */
const STRINGS: Record<Locale, Record<string, string>> = { de, en, fr }

export function useInsuranceTranslation(): {
  t: (key: string, params?: Record<string, string>) => string
  locale: Locale
} {
  const rawLocale = useLocale()
  const locale: Locale =
    rawLocale === "en" || rawLocale === "fr" ? rawLocale : "de"

  // The membership-first site names its two MRI packages its own way.
  const { code: country } = useCountry()
  const strings = useMemo(() => {
    const site = membershipFirstCheckerStrings(locale, country)
    return site ? { ...STRINGS[locale], ...site } : STRINGS[locale]
  }, [locale, country])

  const t = useCallback(
    (key: string, params?: Record<string, string>): string => {
      let text = strings[key]
      if (text === undefined) return key
      if (params) {
        for (const [param, value] of Object.entries(params)) {
          text = text.replace(new RegExp(`\\{\\{${param}\\}\\}`, "g"), value)
        }
      }
      return text
    },
    [strings]
  )

  return { t, locale }
}
